import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  listTickets,
  updateTicket,
  type SupportTicketItem,
  type TicketStatusUpper,
} from '@/lib/operatorApi';
import { relativeTime } from '@/lib/format';

const STATUS_FILTERS: { value: TicketStatusUpper | 'all'; label: string }[] = [
  { value: 'all',         label: 'Все' },
  { value: 'OPEN',        label: 'Открытые' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'RESOLVED',    label: 'Решённые' },
];

const STATUS_LABEL: Record<TicketStatusUpper, { label: string; cls: string }> = {
  OPEN:        { label: 'Открыт',   cls: 'badge-amber' },
  IN_PROGRESS: { label: 'В работе', cls: 'badge-orange' },
  RESOLVED:    { label: 'Решён',    cls: 'badge-green' },
};

export default function Support() {
  const [tickets, setTickets] = useState<SupportTicketItem[]>([]);
  const [filter, setFilter] = useState<TicketStatusUpper | 'all'>('all');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listTickets(filter, 200)
      .then((r) => setTickets(r.results))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id: string, status: TicketStatusUpper) => {
    try {
      setBusyId(id);
      await updateTicket(id, { status });
      load();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Поддержка"
        title="Обращения клиентов"
        description="Email, WhatsApp и форма с лендинга приходят сюда. Отвечаем в течение рабочего дня."
        actions={
          <button type="button" className="btn-ghost" onClick={load} disabled={loading}>
            <RefreshCw size={13} strokeWidth={1.75} />
            Обновить
          </button>
        }
      />

      <div className="page-pad">
        {error && (
          <div className="mb-4 border border-red-600/30 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={
                'border px-3 py-1.5 text-[12px] font-medium transition ' +
                (filter === f.value
                  ? 'border-ink bg-ink text-white'
                  : 'border-line bg-white text-ink/70 hover:border-ink/50')
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Desktop: таблица */}
        <div className="card hidden md:block">
          <table className="table-grid">
            <thead>
              <tr>
                <th>Тема</th>
                <th>Арендатор</th>
                <th>Канал</th>
                <th>Статус</th>
                <th className="text-right">Последнее сообщение</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const s = STATUS_LABEL[t.status];
                return (
                  <tr key={t.id}>
                    <td className="max-w-[420px] truncate font-medium">{t.subject}</td>
                    <td>
                      {t.tenantId ? (
                        <Link to={`/tenants/${t.tenantId}`} className="hover:underline">
                          {t.tenantName}
                        </Link>
                      ) : (
                        <span className="text-ink/50">{t.from.email ?? t.from.name ?? '—'}</span>
                      )}
                    </td>
                    <td className="text-ink/70">{t.channel.toLowerCase()}</td>
                    <td>
                      <span className={s.cls}>{s.label}</span>
                    </td>
                    <td className="text-right font-mono text-[12px] text-ink/60">
                      {relativeTime(t.lastMessageAt)}
                    </td>
                    <td className="text-right">
                      {t.status !== 'RESOLVED' && (
                        <div className="flex justify-end gap-2 text-[12px]">
                          {t.status === 'OPEN' && (
                            <button
                              className="text-ink/70 hover:text-ink"
                              onClick={() => changeStatus(t.id, 'IN_PROGRESS')}
                              disabled={busyId === t.id}
                            >
                              <Loader2 size={12} className="mr-1 inline" />В работу
                            </button>
                          )}
                          <button
                            className="text-green-700 hover:underline"
                            onClick={() => changeStatus(t.id, 'RESOLVED')}
                            disabled={busyId === t.id}
                          >
                            <CheckCircle2 size={12} className="mr-1 inline" />Закрыть
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-ink/50">
                    Обращений нет
                  </td>
                </tr>
              )}
              {loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-ink/40">
                    Загрузка…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: карточки */}
        <ul className="card divide-y divide-line md:hidden">
          {tickets.map((t) => {
            const s = STATUS_LABEL[t.status];
            return (
              <li key={t.id} className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-medium text-ink line-clamp-2">
                      {t.subject}
                    </div>
                    <div className="mt-1 truncate text-[12px] text-ink/60">
                      {t.tenantId ? (
                        <Link to={`/tenants/${t.tenantId}`} className="hover:underline">
                          {t.tenantName}
                        </Link>
                      ) : (
                        t.from.email ?? t.from.name ?? '—'
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-ink/50">
                      {t.channel.toLowerCase()} · {relativeTime(t.lastMessageAt)}
                    </div>
                  </div>
                  <span className={`${s.cls} shrink-0`}>{s.label}</span>
                </div>

                {t.status !== 'RESOLVED' && (
                  <div className="mt-3 flex flex-wrap justify-end gap-3 text-[13px]">
                    {t.status === 'OPEN' && (
                      <button
                        className="inline-flex items-center gap-1 text-ink/70 hover:text-ink"
                        onClick={() => changeStatus(t.id, 'IN_PROGRESS')}
                        disabled={busyId === t.id}
                      >
                        <Loader2 size={13} />
                        В работу
                      </button>
                    )}
                    <button
                      className="inline-flex items-center gap-1 font-medium text-green-700 hover:underline"
                      onClick={() => changeStatus(t.id, 'RESOLVED')}
                      disabled={busyId === t.id}
                    >
                      <CheckCircle2 size={13} />
                      Закрыть
                    </button>
                  </div>
                )}
              </li>
            );
          })}
          {!loading && tickets.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/50">Обращений нет</li>
          )}
          {loading && tickets.length === 0 && (
            <li className="px-4 py-10 text-center text-[13px] text-ink/40">Загрузка…</li>
          )}
        </ul>
      </div>
    </div>
  );
}
